import { getItemCategories, IItemCategory, IPricing } from "@/api/settings";
import { DBStoreOrder } from "@/api/store.ts";
import { FormBox } from "@/components/dashboard/forms/FormContainers.tsx";
import { AddressAutoComplete } from "@/components/dashboard/orders/process/AddressAutoComplete.tsx";
import {
    Box,
    Divider,
    FormGroup,
    InputAdornment,
    Stack,
    Typography,
} from "@mui/material";
import { useAsync, useMountEffect } from "@react-hookz/web";
import {
    FormEmailElement,
    FormPhoneNumberElement,
    FormRadioGroup,
    FormSelectElement,
    FormTextFieldElement,
} from "@rhf-kit/mui";
import { FormButton as Button } from "@rhf-kit/mui/dist/FormButton/FormButton";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ItemCategoryAutoComplete } from "./ItemCategoryAutoComplete";

interface IProps {
    error: Error | null;
    loading?: boolean;
    order?: DBStoreOrder;
    pricing?: IPricing;
    invoice?: boolean;
    itemLength?: number;
    itemWidth?: number;
    itemHeight?: number;
}

const flattenCategories = (categories: IItemCategory[]): IItemCategory[] => {
    let flattened: IItemCategory[] = [];

    categories.forEach((category) => {
        flattened.push(category);
        if (category.children.length > 0) {
            flattened = flattened.concat(flattenCategories(category.children));
        }
    });

    return flattened;
};

export const OrderFormElements = ({
    error,
    order,
    loading,
    invoice,
}: IProps) => {
    const { setError, watch, setValue } = useFormContext();

    const watchRequiredToBePresent = watch("requiredToBePresent");
    const itemLength = watch("itemLength");
    const itemWidth = watch("itemWidth");
    const itemHeight = watch("itemHeight");

    // options for item size select
    const itemSizeOptions = [
        { value: "<34,560in³", label: "Small (<34,560in³)", max: 34560 },
        {
            value: "34,561 - 90,720in³",
            label: "Medium (34,561 - 90,720in³)",
            max: 90720,
        },
        {
            value: "90,721 - 156,672in³",
            label: "Large (90,721 - 156,672in³)",
            max: 156672,
        },
        {
            value: ">156,672in³",
            label: "Extra Large (>156,672in³)",
            max: Infinity,
        },
    ];

    const calculatedVolume =
        Number(itemLength) * Number(itemWidth) * Number(itemHeight);
    const matchedSizeOption = itemSizeOptions.find(
        (opt) => calculatedVolume <= opt.max
    );
    if (matchedSizeOption) {
        setValue("itemSize", matchedSizeOption.value, { shouldValidate: true });
    }

    // options for floor number select
    const floorNumberOptions = [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "3+" },
    ];

    //   disabled delivery window options - not used for now
    //   const deliveryWindowOptions = [
    //     { value: "7 Days", label: "7 days (default)" },
    //     { value: "5 Days", label: "5 Days (+$50.00)" },
    //     { value: "3 Days", label: "3 Days (+$75.00)" },
    //     { value: "2 Days", label: "2 Days (+$150.00)" },
    //   ];

    const [getItemCategoriesState, getItemCategoriesActions] =
        useAsync(getItemCategories);

    // item category options (set in useEffect below)
    const [itemCategoryOptions, setItemCategoryOptions] = useState<
        IItemCategory[]
    >([]);

    useMountEffect(() => {
        getItemCategoriesActions.execute();
    });

    useEffect(() => {
        if (
            getItemCategoriesState.status === "success" &&
            getItemCategoriesState.result
        ) {
            setItemCategoryOptions(getItemCategoriesState.result.items);
        }
    }, [getItemCategoriesState]);

    useEffect(() => {
        if (error) {
            const axiosError = error as AxiosError<{
                detail: string;
            }>;

            if (
                axiosError.response &&
                axiosError.response.data &&
                axiosError.response.data.detail ===
                    "Apartment or suite number is missing."
            ) {
                setError("streetAddress2", {
                    type: "manual",
                    message: "Apartment or suite number is missing.",
                });

                const addressElement = document.getElementById(
                    "address-auto-complete"
                );

                addressElement?.scrollIntoView();
            }
        }
    }, [setError, error]);

    // useEffect(() => {
    //     if (pricing) {
    //         if (watchItemSize) {
    //             setItemSizePrice(
    //                 getItemSizePrice({
    //                     size: watchItemSize,
    //                     pricing,
    //                 })
    //             );
    //         }
    //         if (watchItemQuantity) {
    //             setItemQuantityPrice(
    //                 getItemQuantityPrice({
    //                     pricing,
    //                     quantity: watchItemQuantity,
    //                     size: watchItemSize,
    //                 })
    //             );
    //         }
    //         if (watchItemFloor) {
    //             setItemFloorPrice(
    //                 getItemFloorPrice({
    //                     pricing,
    //                     floor: watchItemFloor,
    //                 })
    //             );
    //         }
    //         setRequiresAssemblyPrice(
    //             getAssemblyPrice({
    //                 pricing,
    //                 requiresAssembly: watchRequiresAssembly,
    //             })
    //         );
    //         setMaxWeightPrice(
    //             getMaxWeightPrice({
    //                 pricing,
    //                 isMaxWeight: watchWeightLimit,
    //             })
    //         );
    //         setValue(
    //             "estimatedValue",
    //             handleEstimatedValueChange(watchEstimatedValue)
    //         );
    //     }
    // }, [
    //     watchItemSize,
    //     pricing,
    //     watchItemQuantity,
    //     watchItemFloor,
    //     watchRequiresAssembly,
    //     watchWeightLimit,
    //     watchEstimatedValue,
    //     setValue,
    // ]);

    return (
        <FormBox>
            <Divider>Contact Information</Divider>
            <Box>
                {/* First Name Input */}
                <FormTextFieldElement
                    name={"firstName"}
                    label="First Name"
                    margin={"normal"}
                    fullWidth
                    required
                />

                {/* Last Name Input */}
                <FormTextFieldElement
                    name={"lastName"}
                    label={"Last name"}
                    margin={"normal"}
                    fullWidth
                    required
                />

                {/* Email Input */}
                <FormEmailElement
                    name={"email"}
                    label={"Email"}
                    margin={"normal"}
                    renderIcon={false}
                    fullWidth
                    required
                />

                {/* Phone Number Input */}
                <FormPhoneNumberElement
                    name={"phoneNumber"}
                    label={"Phone Number"}
                    margin={"normal"}
                    placeholder={"(XXX) XXX-XXXX"}
                    renderIcon={false}
                    fullWidth
                    required
                />

                {/* Add Referral */}
                <FormTextFieldElement
                    label="Referral / Order Number"
                    margin={"normal"}
                    fullWidth
                    name={"addReferral"}
                />

                <Divider>Delivery Location</Divider>
                {/* Destination Address Input */}
                <AddressAutoComplete />

                {/* Apartment Number Input */}
                <FormTextFieldElement
                    name={"streetAddress2"}
                    label={"Apartment Number (Optional)"}
                    margin={"normal"}
                    fullWidth
                />

                {/* City Input */}
                <FormTextFieldElement
                    name={"city"}
                    label={"City"}
                    margin={"normal"}
                    fullWidth
                    required
                />

                <Box display={"flex"}>
                    {/* State Input */}
                    <Box flex={1} mr={1}>
                        <FormTextFieldElement
                            name={"state"}
                            label={"State"}
                            margin={"normal"}
                            fullWidth
                            required
                        />
                    </Box>
                    {/* Zip Code Input */}
                    <Box flex={1} ml={1}>
                        <FormTextFieldElement
                            name={"zipCode"}
                            label={"Zip Code"}
                            margin={"normal"}
                            fullWidth
                            required
                        />
                    </Box>
                </Box>

                <Divider>Item Information</Divider>

                {/* Item Description */}
                <ItemCategoryAutoComplete
                    itemCategories={flattenCategories(itemCategoryOptions)}
                />

                <Divider sx={{ marginTop: ".6rem", marginBottom: ".6rem" }} />
                {/* Estimated Value Input */}
                <FormTextFieldElement
                    name={"estimatedValue"}
                    label={"Estimated Value of Items (USD)"}
                    margin={"normal"}
                    helperText={"For insurance reasons"}
                    fullWidth
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                        ),
                    }}
                />

                <Divider sx={{ marginTop: ".6rem", marginBottom: ".6rem" }} />

                <Box display={"flex"}>
                    {/* Item Custom Size Length */}
                    <Box flex={1}>
                        <FormTextFieldElement
                            name={"itemLength"}
                            label={"Item Length (in)"}
                            margin={"normal"}
                            type={"number"}
                            fullWidth
                            required
                        />
                    </Box>
                    {/* Item Custom Size Width */}
                    <Box flex={1} ml={1}>
                        <FormTextFieldElement
                            name={"itemWidth"}
                            label={"Item Width (in)"}
                            margin={"normal"}
                            type={"number"}
                            fullWidth
                            required
                        />
                    </Box>
                    {/* Item Custom Size Height */}
                    <Box flex={1} ml={1}>
                        <FormTextFieldElement
                            name={"itemHeight"}
                            label={"Item Height (in)"}
                            margin={"normal"}
                            type={"number"}
                            fullWidth
                            required
                        />
                    </Box>
                </Box>

                <Divider sx={{ marginTop: ".6rem", marginBottom: ".6rem" }} />
                {/* Quantity Input */}
                <FormTextFieldElement
                    name={"itemQuantity"}
                    label={"Item Quantity"}
                    margin={"normal"}
                    type={"number"}
                    helperText={
                        "If you have multiple items in a box, each box counts as 1 item the size of the box."
                    }
                    rules={{
                        min: {
                            value: 1,
                            message: "Item Quantity must be at least 1",
                        },
                    }}
                    fullWidth
                    required
                />

                <Divider sx={{ marginTop: ".6rem", marginBottom: ".6rem" }} />

                {/* Floor Number Select */}
                <FormSelectElement
                    name={"itemFloor"}
                    label={"Floor Number"}
                    options={floorNumberOptions}
                    formControlProps={{ margin: "normal" }}
                    fullWidth
                    required
                />

                <Divider sx={{ marginTop: ".6rem", marginBottom: ".6rem" }} />

                <FormGroup>
                    <Typography>
                        Do you want your item left inside or outside?
                    </Typography>
                    <Stack
                        direction={"row"}
                        sx={{ marginLeft: "1rem", gap: "1rem" }}
                    >
                        <FormRadioGroup
                            label="Delivery Area"
                            name="insideOrOutside"
                            options={[
                                {
                                    label: "Outside",
                                    value: 1,
                                },
                                {
                                    label: "Inside",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: "#666",
                        }}
                    >
                        If you select outside, your item will be left in a dry
                        and covered area.
                    </Typography>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />
                    <Typography>
                        Do you need to be present for drop off?
                    </Typography>
                    <Stack direction={"row"} sx={{ marginLeft: "1rem" }}>
                        <FormRadioGroup
                            label="Required to be Present"
                            name="requiredToBePresent"
                            options={[
                                {
                                    label: "Yes",
                                    value: 1,
                                },
                                {
                                    label: "No",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />

                    <Typography>Does your item require assembly?</Typography>
                    <Stack direction={"row"} sx={{ marginLeft: "1rem" }}>
                        <FormRadioGroup
                            label="Requires Assembly"
                            name="requiresAssembly"
                            options={[
                                {
                                    label: "Yes",
                                    value: 1,
                                },
                                {
                                    label: "No",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />

                    <Typography>
                        Does your item weigh more than 100 lbs?
                    </Typography>
                    <Stack direction={"row"} sx={{ marginLeft: "1rem" }}>
                        <FormRadioGroup
                            label="Weight Limit"
                            name="weightLimit"
                            options={[
                                {
                                    label: "Yes",
                                    value: 1,
                                },
                                {
                                    label: "No",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />

                    <Typography>Is there an elevator available?</Typography>
                    <Stack direction={"row"} sx={{ marginLeft: "1rem" }}>
                        <FormRadioGroup
                            label="Elevator Available"
                            name="elevatorAvailable"
                            options={[
                                {
                                    label: "Yes",
                                    value: 1,
                                },
                                {
                                    label: "No",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />

                    <Typography>Would you like additional wrapping?</Typography>
                    <Stack direction={"row"} sx={{ marginLeft: "1rem" }}>
                        <FormRadioGroup
                            label="Needs Wrapped"
                            name="needsWrapped"
                            options={[
                                {
                                    label: "Yes",
                                    value: 1,
                                },
                                {
                                    label: "No",
                                    value: 0,
                                },
                            ]}
                            row
                            required
                            formLabelProps={{ sx: { display: "none" } }}
                            formHelperTextProps={{ sx: { margin: 0 } }}
                        />
                    </Stack>
                    <Divider
                        sx={{ marginTop: ".6rem", marginBottom: ".6rem" }}
                    />
                </FormGroup>

                {/* Dropoff Instructions */}
                <FormTextFieldElement
                    name={"shippingInstructions"}
                    label={"Shipment Instructions"}
                    margin={"normal"}
                    minRows={3}
                    multiline
                    fullWidth
                    required={invoice}
                    sx={{}}
                />

                {/* Dropoff Instructions */}
                <FormTextFieldElement
                    name={"dropoffInstructions"}
                    label={"Dropoff Instructions"}
                    margin={"normal"}
                    minRows={3}
                    multiline
                    fullWidth
                    required={!watchRequiredToBePresent}
                />

                {/* price breakdown */}

                {/* {!order && watchItemQuantity > 0 && (
                    <OrderPriceBreakdown
                        watchedData={{
                            watchItemSize,
                            watchItemQuantity,
                            watchItemFloor,
                            watchRequiresAssembly,
                            watchWeightLimit,
                            watchElevatorAvailable,
                        }}
                        formData={{
                            itemSizePrice,
                            itemQuantityPrice,
                            itemFloorPrice,
                            requiresAssemblyPrice,
                            maxWeightPrice,
                        }}
                    />
                )} 
                {!order && (
                    <Typography variant="caption" color="textSecondary">
                        *Distance fee calculated at checkout
                    </Typography>
                )} */}
            </Box>

            <Button
                variant="contained"
                color="primary"
                sx={{
                    mt: 2,
                    backgroundColor: "#40ABBA",
                    ":hover": { backgroundColor: "#2a6574" },
                }}
                loading={loading}
                loadingPosition={"end"}
            >
                {order ? "Update" : "Continue"}
            </Button>
        </FormBox>
    );
};
